// Images for Hive storage are shrunk to WebP, at most 2,560 px wide, before
// upload (see "How we stay free" in the design doc).

export const MAX_IMAGE_WIDTH = 2560;

/** The size to scale an image to: never wider than `max`, aspect ratio kept, never enlarged. */
export function scaledSize(width: number, height: number, max = MAX_IMAGE_WIDTH): { width: number; height: number } {
  if (width <= max) return { width, height };
  return { width: max, height: Math.round((height * max) / width) };
}

/** Whether an image type is worth re-encoding (not GIFs, which may animate, or SVGs). */
export function shouldCompress(type: string): boolean {
  return /^image\/(png|jpe?g|webp|bmp)$/i.test(type);
}

/** Re-encodes an image as WebP no wider than 2,560 px. Returns the original if that wouldn't help. */
export async function compressImage(file: File): Promise<{ file: File; width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const size = scaledSize(bitmap.width, bitmap.height);
  if (!shouldCompress(file.type)) {
    bitmap.close();
    return { file, width: size.width, height: size.height };
  }
  const canvas = new OffscreenCanvas(size.width, size.height);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close();
  const blob = await canvas.convertToBlob({ type: 'image/webp', quality: 0.86 });
  if (blob.size >= file.size && size.width === bitmap.width) return { file, ...size };
  const name = file.name.replace(/\.[^.]+$/, '') + '.webp';
  return { file: new File([blob], name, { type: 'image/webp' }), ...size };
}
